/**
 * Certification Modal Handler
 * Handles opening/closing and tab navigation for the IBE certification info modal
 */

(function() {
    'use strict';

    class CertificationModal {
        constructor(container) {
            this.container = container;
            this.modal = container.querySelector('[data-element="cert-modal"]');
            this.openButtons = container.querySelectorAll('[data-element="cert-modal-open"]');
            this.closeButton = container.querySelector('[data-element="cert-modal-close"]');
            this.closeButtonDesktop = container.querySelector('[data-element="cert-modal-close-desktop"]');
            this.overlay = container.querySelector('[data-element="cert-modal-overlay"]');
            this.tabs = container.querySelectorAll('[data-element="cert-tab"]');
            this.panels = container.querySelectorAll('[data-panel]');
            this.activeTab = null;
            this.isOpen = false;
            this.scrollY = 0;
            this.boundKeydown = null;

            this.init();
        }

        init() {
            if (!this.modal) return;
            this.bindEvents();
            this.setInitialTab();
        }

        bindEvents() {
            // Open modal
            this.openButtons.forEach(button => {
                button.addEventListener('click', () => this.open());
            });

            // Close modal via buttons/overlay
            [this.closeButton, this.closeButtonDesktop, this.overlay].forEach((target) => {
                if (!target) return;
                target.addEventListener('click', () => this.close());
            });

            // Tab switching
            this.tabs.forEach(tab => {
                tab.addEventListener('click', () => {
                    const tabName = tab.getAttribute('data-tab');
                    this.switchTab(tabName);
                });
            });

            // Escape key to close
            this.boundKeydown = (e) => {
                if (e.key === 'Escape' && this.isOpen) {
                    this.close();
                }
            };
            document.addEventListener('keydown', this.boundKeydown);
        }

        setInitialTab() {
            const firstTab = this.tabs[0];
            if (!firstTab) return;
            const tabName = firstTab.getAttribute('data-tab');
            this.switchTab(tabName);
        }

        setModalOpenState(isOpen) {
            if (isOpen) {
                // Use requestAnimationFrame to ensure initial state is applied before transition
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        this.modal.classList.add('stylist-locator__cert-modal--open');
                    });
                });
            } else {
                this.modal.classList.remove('stylist-locator__cert-modal--open');
            }
        }

        open() {
            if (this.isOpen) return;

            this.setModalOpenState(true);
            document.body.classList.add('stylist-locator__cert-modal-open');
            this.isOpen = true;

            // Prevent body scroll
            this.lockBodyScroll();

            // Focus management - prefer desktop button if visible, otherwise mobile
            const focusButton = this.closeButtonDesktop || this.closeButton;
            focusButton?.focus();
        }

        close() {
            if (!this.isOpen) return;

            this.setModalOpenState(false);
            document.body.classList.remove('stylist-locator__cert-modal-open');
            this.isOpen = false;

            // Restore body scroll
            this.unlockBodyScroll();

            // Return focus to the button that opened the modal
            if (this.openButtons.length > 0) {
                this.openButtons[0].focus();
            }
        }

        switchTab(tabName) {
            if (!tabName) return;

            // Update tabs
            this.tabs.forEach(tab => {
                const isActive = tab.getAttribute('data-tab') === tabName;
                tab.classList.toggle('stylist-locator__cert-modal-tab--active', isActive);
            });

            // Update panels
            this.panels.forEach(panel => {
                const isActive = panel.getAttribute('data-panel') === tabName;
                panel.classList.toggle('stylist-locator__cert-modal-panel--active', isActive);
            });

            this.activeTab = tabName;
        }

        lockBodyScroll() {
            // Store current scroll position
            this.scrollY = window.scrollY || 0;
            
            // Apply styles to prevent scrolling
            document.body.style.position = 'fixed';
            document.body.style.top = `-${this.scrollY}px`;
            document.body.style.width = '100%';
        }

        unlockBodyScroll() {
            // Remove fixed positioning
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            
            // Restore scroll position
            if (this.scrollY !== undefined) {
                window.scrollTo(0, this.scrollY);
            }
        }
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initModal);
    } else {
        initModal();
    }

    function initModal() {
        const containers = document.querySelectorAll('.js-stylist-locator');
        containers.forEach(container => {
            new CertificationModal(container);
        });
    }

})();


